import React, { useMemo } from 'react';

import { TextField } from 'Containers/App/types';
import { ClaimsSummaryState, ClaimsSummary } from 'Containers/ProfilePage/types';
import { ContentfulInfoBar } from 'ContentfulWrappers/ContentfulInfoBar';
import InfoBar from 'DesignLibrary/molecules/InfoBar';
import { InfoBarLoader } from 'DesignLibrary/molecules/InfoBarLoader';
import RichText from 'Shared/RichText';
import { toDollars } from 'Utils/helpers';

export interface ClaimsSummaryBarProps {
  claimsSummary: ClaimsSummary | null;
  claimsSummaryState: ClaimsSummaryState;
  claimsError: Error | null;
  field: TextField;
}

export const ClaimsSummaryBar = ({
  field,
  claimsSummary,
  claimsError,
  claimsSummaryState,
}: ClaimsSummaryBarProps) => {
  const errorVars = useMemo(
    () => ({ x: claimsError?.message ? ` ${claimsError?.message}` : '' }),
    [claimsError],
  );
  const successVars = useMemo(() => {
    const { oopTowardMaxDollars, maxDollars, oopTowardDeductibleDollars, deductibleDollars } =
      claimsSummary || {};
    return {
      oopSpent: toDollars(oopTowardMaxDollars, 2),
      oopMax: toDollars(maxDollars, 0),
      deductibleSpent: toDollars(oopTowardDeductibleDollars, 2),
      deductibleTotal: toDollars(deductibleDollars, 0),
    };
  }, [claimsSummary]);

  const InfoBar =
    claimsSummaryState === ClaimsSummaryState.Error ? (
      <ErrorInfoBar vars={errorVars} />
    ) : (
      <SuccessInfoBar field={field} vars={successVars} />
    );

  const isLoading = claimsSummaryState === ClaimsSummaryState.Loading;

  if (claimsSummaryState === ClaimsSummaryState.NotAvailable) {
    return null;
  }
  return (
    <div data-testid="claims-summary-bar">
      <InfoBarLoader isLoading={isLoading} field="claims_summary_bar.loading_message">
        {InfoBar}
      </InfoBarLoader>
    </div>
  );
};

const SuccessInfoBar = ({ vars, field }) => {
  return <ContentfulInfoBar field={field} vars={vars} />;
};

const ErrorInfoBar = ({ vars }) => {
  const field = 'claims_summary_bar.error_message';
  return (
    <InfoBar
      text={<RichText noWrapper field={field} vars={vars} />}
      color="yellow"
      key={field}
      icon="Warning"
    />
  );
};
